import axios from "axios";

const api = axios.create({
  baseURL: "/",
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (typeof window !== "undefined") {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (data: { email?: string; phone?: string; password: string }) =>
    api.post("/api/auth/login", data),
  register: (data: { email: string; phone?: string; password: string; firstName: string; lastName: string; role?: string }) =>
    api.post("/api/auth/register", data),
  logout: (data: { refreshToken: string }) =>
    api.post("/api/auth/logout", data),
  refreshToken: (data: { refreshToken: string }) =>
    api.post("/api/auth/refresh-token", data),
  forgotPassword: (data: { email: string }) =>
    api.post("/api/auth/forgot-password", data),
  resetPassword: (data: { token: string; newPassword: string }) =>
    api.post("/api/auth/reset-password", data),
  getMe: () => api.get("/api/auth/me"),
  updateProfile: (data: Record<string, unknown>) => api.put("/api/auth/profile", data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put("/api/auth/change-password", data),
};

export const userApi = {
  getAll: (params?: { role?: string; search?: string; page?: number; limit?: number }) =>
    api.get("/api/users", { params }),
  getById: (id: string) => api.get(`/api/users/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/users/${id}`, data),
  deactivate: (id: string) => api.delete(`/api/users/${id}`),
  resetPassword: (id: string, newPassword: string) =>
    api.post(`/api/users/${id}/reset-password`, { newPassword }),
};

export const firmApi = {
  getAll: () => api.get("/api/firms"),
  getById: (id: string) => api.get(`/api/firms/${id}`),
  create: (data: Record<string, unknown>) => api.post("/api/firms", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/firms/${id}`, data),
};

export const vehicleApi = {
  getAll: (firmId: string) => api.get(`/api/firms/${firmId}/vehicles`),
  getById: (firmId: string, id: string) => api.get(`/api/firms/${firmId}/vehicles/${id}`),
  create: (firmId: string, data: Record<string, unknown>) => api.post(`/api/firms/${firmId}/vehicles`, data),
  update: (firmId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/vehicles/${id}`, data),
  delete: (firmId: string, id: string) => api.delete(`/api/firms/${firmId}/vehicles/${id}`),
};

export const driverApi = {
  getAll: (firmId: string) => api.get(`/api/firms/${firmId}/drivers`),
  getById: (firmId: string, id: string) => api.get(`/api/firms/${firmId}/drivers/${id}`),
  create: (firmId: string, data: Record<string, unknown>) => api.post(`/api/firms/${firmId}/drivers`, data),
  update: (firmId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/drivers/${id}`, data),
  delete: (firmId: string, id: string) => api.delete(`/api/firms/${firmId}/drivers/${id}`),
};

export const routeApi = {
  getAll: (firmId: string) => api.get(`/api/firms/${firmId}/routes`),
  getById: (firmId: string, id: string) => api.get(`/api/firms/${firmId}/routes/${id}`),
  create: (firmId: string, data: Record<string, unknown>) => api.post(`/api/firms/${firmId}/routes`, data),
  update: (firmId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/routes/${id}`, data),
  delete: (firmId: string, id: string) => api.delete(`/api/firms/${firmId}/routes/${id}`),
  addStop: (firmId: string, routeId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/routes/${routeId}/stops`, data),
  updateStop: (firmId: string, routeId: string, stopId: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/routes/${routeId}/stops/${stopId}`, data),
  deleteStop: (firmId: string, routeId: string, stopId: string) =>
    api.delete(`/api/firms/${firmId}/routes/${routeId}/stops/${stopId}`),
};

export const passengerApi = {
  getAll: (firmId: string, params?: Record<string, string>) =>
    api.get(`/api/firms/${firmId}/passengers`, { params }),
  getById: (firmId: string, id: string) => api.get(`/api/firms/${firmId}/passengers/${id}`),
  create: (firmId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/passengers`, data),
  update: (firmId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/passengers/${id}`, data),
  delete: (firmId: string, id: string) => api.delete(`/api/firms/${firmId}/passengers/${id}`),
  import: (firmId: string, passengers: Record<string, unknown>[]) =>
    api.post(`/api/firms/${firmId}/passengers/import`, { passengers }),
};

export const attendanceApi = {
  record: (firmId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/attendance/record`, data),
  getAll: (firmId: string, params?: Record<string, string>) =>
    api.get(`/api/firms/${firmId}/attendance`, { params }),
  getReport: (firmId: string, params?: Record<string, string>) =>
    api.get(`/api/firms/${firmId}/attendance/report`, { params }),
};

export const paymentApi = {
  getAll: (firmId: string, params?: Record<string, string>) =>
    api.get(`/api/firms/${firmId}/payments`, { params }),
  create: (firmId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/payments`, data),
  update: (firmId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/payments/${id}`, data),
  getReport: (firmId: string, params?: Record<string, string>) =>
    api.get(`/api/firms/${firmId}/payments/report`, { params }),
  generateMonthly: (firmId: string, data: { month: number; year: number }) =>
    api.post(`/api/firms/${firmId}/payments/generate-monthly`, data),
};

export const dashboardApi = {
  get: (firmId: string) => api.get(`/api/firms/${firmId}/dashboard`),
};

export const notificationApi = {
  getAll: () => api.get("/api/notifications"),
  markRead: (id: string) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put("/api/notifications/read-all"),
  getPreferences: () => api.get("/api/notifications/preferences"),
};

export const complaintApi = {
  getAll: (firmId: string) => api.get(`/api/firms/${firmId}/complaints`),
  create: (data: Record<string, unknown>) => api.post("/api/complaints", data),
  getById: (firmId: string, id: string) => api.get(`/api/firms/${firmId}/complaints/${id}`),
  respond: (firmId: string, id: string, response: string) =>
    api.put(`/api/firms/${firmId}/complaints/${id}/respond`, { response }),
  updateStatus: (firmId: string, id: string, status: string) =>
    api.put(`/api/firms/${firmId}/complaints/${id}/status`, { status }),
};

export const documentApi = {
  getAll: (firmId: string, params?: Record<string, string>) =>
    api.get(`/api/firms/${firmId}/documents`, { params }),
  create: (firmId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/documents`, data),
  delete: (firmId: string, id: string) =>
    api.delete(`/api/firms/${firmId}/documents/${id}`),
};

export const settlementApi = {
  getAll: (firmId: string) => api.get(`/api/firms/${firmId}/settlements`),
  getById: (firmId: string, id: string) => api.get(`/api/firms/${firmId}/settlements/${id}`),
  calculate: (firmId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/settlements/calculate`, data),
  update: (firmId: string, id: string, data: Record<string, unknown>) =>
    api.put(`/api/firms/${firmId}/settlements/${id}`, data),
  generateMonthly: (firmId: string, data: Record<string, unknown>) =>
    api.post(`/api/firms/${firmId}/settlements/generate-monthly`, data),
};

export default api;
